import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import type { SupplyPricePeriod } from '@obraplan/core/application/ports/requisition';
import { messageForCode } from '@obraplan/core/application/errors/error-codes';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import type { ProjectId } from '@obraplan/core/domain/shared/ids';
import { useContainer, usePermission, useSessionState } from '@/features/session/use-session';
import {
  canonicalUnit,
  materialCodeKey,
  productFactorKey,
  type ProductUnitFactor,
} from '@obraplan/core/domain/takeoff/material-price';
import { periodoDeDozeMeses } from './preco-do-compras';
import {
  codigosDosPrecos,
  comFatores,
  comPreco,
  codigosSemFoto,
  comPrecos,
  contarFotos,
  emLotes,
  mapaDePrecos,
  type DecisaoDeFator,
  type DecisaoDePreco,
  type FatoresDosProdutos,
  type PrecosDaObra,
  type ResultadoDaAtualizacao,
} from './precos-da-obra';

/** O que uma rodada do "Atualizar preços" fez — com a recusa, quando houve, e o que já tinha ido. */
export interface AtualizacaoFeita extends ResultadoDaAtualizacao {
  /** A mensagem da recusa; os lotes antes dela já gravaram, e a contagem os inclui. */
  readonly erro?: string;
}

/**
 * Os preços de material da obra aberta, e as duas ações sobre eles (Fase 29, etapa 2).
 *
 * É o que a aba de itens passa às duas tabelas — a de itens por serviço e a consolidação —
 * para que as duas leiam **o mesmo** preço de cada material (D7) e a gravação de um apareça
 * nas duas sem rebusca.
 */
export interface PrecosDaObraNaTela {
  /** A obra dos preços lidos — ausente enquanto não leu. */
  readonly obra?: ProjectId;
  /** `undefined` = ainda lendo, ou a leitura falhou (`erro` diz). */
  readonly precos: PrecosDaObra | undefined;
  /** O período da foto mais recente (D4); sem foto nenhuma, os doze meses até hoje. */
  readonly periodo: SupplyPricePeriod;
  /**
   * `takeoff.manage` (D13): quem só vê o quantitativo vê os preços, e não os mexe. O orçamento
   * aprovado não trava o preço: o serviço aprovado lê o da foto (Fase 33, D8).
   */
  readonly podeEditar: boolean;
  readonly atualizando: boolean;
  /** A leitura que não veio — dita, para "sem preço" não parecer a resposta. */
  readonly erro?: string;
  /**
   * Grava o que alguém decidiu num material e troca a linha pela que o servidor devolveu.
   * `false` = recusado; o aviso já foi dado.
   */
  readonly definir: (key: string, decisao: DecisaoDePreco, code?: string) => Promise<boolean>;
  /** Tira a foto do compras destes códigos, em lotes de 500 (RN-394). */
  readonly atualizar: (
    codes: readonly string[],
    periodo: SupplyPricePeriod,
  ) => Promise<AtualizacaoFeita | undefined>;
  /**
   * Os fatores dos produtos que a obra usa, por `productFactorKey` (decidido em 13/10/2026):
   * confirmados uma vez — na importação, ou no diálogo do preço —, valem em todas as obras.
   * Vazio enquanto lê; a falha da leitura deixa o material "falta o fator", que é o que ele
   * diria sem fator nenhum, e o diálogo deixa confirmar.
   */
  readonly fatoresDoProduto: FatoresDosProdutos;
  /** Grava o fator do produto — para todas as obras. `false` = recusado; o aviso já foi dado. */
  readonly definirFatorDoProduto: (fator: DecisaoDeFator) => Promise<boolean>;
}

interface Lidos {
  readonly de: ProjectId;
  readonly precos: PrecosDaObra;
  readonly periodo?: SupplyPricePeriod;
}

interface FatoresLidos {
  readonly de: ProjectId;
  readonly fatores: FatoresDosProdutos;
}

/** O mesmo mapa vazio a cada quadro: um novo por renderização refaria o preço de tudo. */
const SEM_FATORES: FatoresDosProdutos = new Map();

/**
 * Quem grava preço ou fator por fora deste gancho — a importação, que completa para orçar
 * (13/10/2026) — avisa por aqui, e a tela aberta relê. Sem isto, os fatores confirmados na
 * importação só apareceriam ao trocar de obra.
 */
const ouvintesDePrecos = new Set<(projectId: ProjectId) => void>();

export function avisarPrecosGravados(projectId: ProjectId): void {
  for (const ouvinte of ouvintesDePrecos) ouvinte(projectId);
}

/**
 * Lê os preços uma vez por obra e os mantém em dia com o que esta tela grava.
 *
 * ## Fora do `useCommand`, de propósito
 *
 * O `useCommand` avisa o mundo a cada sucesso (`publishChange`), e toda leitura viva da
 * página rebusca — a estrutura, o quantitativo, o desenho, os serviços. Para um preço isso
 * é trabalho jogado fora: o servidor devolve a linha gravada, e trocá-la no mapa é tudo o
 * que a tela precisa. Pior, a rebusca remontaria a bancada no meio de um lançamento por
 * causa de um número que não é dela. A recusa, porém, **não** é calada: vira o mesmo aviso
 * que o `useCommand` daria.
 *
 * ## A obra que mudou no meio
 *
 * A resposta que chega depois de trocar de obra é descartada: o preço da obra A não pode
 * cair no mapa da obra B, e as chaves das duas são as mesmas (`codigo:e-1`).
 */
export function usePrecosDaObra(): PrecosDaObraNaTela {
  const container = useContainer();
  const { session, activeProjectId } = useSessionState();
  const podeEditar = usePermission(PERMISSION.TAKEOFF_MANAGE, activeProjectId);

  const [lidos, setLidos] = useState<Lidos | undefined>(undefined);
  const [fatores, setFatores] = useState<FatoresLidos | undefined>(undefined);
  const [erro, setErro] = useState<string | undefined>(undefined);
  const [atualizando, setAtualizando] = useState(false);
  /** Sobe quando alguém gravou por fora (`avisarPrecosGravados`): a leitura se refaz. */
  const [releitura, setReleitura] = useState(0);
  // Nasce uma vez: virar a meia-noite com a aba aberta não deve mexer no período proposto.
  const [dozeMeses] = useState(() => periodoDeDozeMeses(new Date()));

  /** A obra aberta agora — o que as respostas atrasadas conferem antes de cair no mapa. */
  const obraAtual = useRef(activeProjectId);
  obraAtual.current = activeProjectId;

  useEffect(() => {
    const ouvinte = (projectId: ProjectId): void => {
      if (projectId === obraAtual.current) setReleitura((vez) => vez + 1);
    };
    ouvintesDePrecos.add(ouvinte);
    return () => {
      ouvintesDePrecos.delete(ouvinte);
    };
  }, []);

  /**
   * Lê os fatores dos produtos destes códigos e os junta aos que a tela já tem.
   *
   * Calada na falha, de propósito: o fator que não veio deixa o material "unidade diferente —
   * falta o fator", que é verdade para quem olha esta tela, e um aviso por cima de cada leitura
   * de preço seria barulho. A chamada passa por `then` para a falha síncrona (o servidor velho,
   * sem o caso de uso) virar rejeição, e não exceção solta.
   */
  const lerFatores = useCallback(
    async (projectId: ProjectId, codes: readonly string[]): Promise<void> => {
      if (session === undefined || codes.length === 0) return;
      const achados: ProductUnitFactor[] = [];
      for (const lote of emLotes(codes)) {
        const resultado = await Promise.resolve()
          .then(() => container.getProductUnitFactors.execute({ projectId, codes: lote }, session))
          .catch(() => undefined);
        if (resultado === undefined || !resultado.ok) return;
        achados.push(...resultado.value);
      }
      if (obraAtual.current !== projectId) return;
      setFatores((atual) => ({
        de: projectId,
        fatores: comFatores(atual?.de === projectId ? atual.fatores : SEM_FATORES, codes, achados),
      }));
    },
    [container, session],
  );

  useEffect(() => {
    if (session === undefined || activeProjectId === undefined) return undefined;
    let vivo = true;
    setErro(undefined);
    container.getProjectMaterialPrices.execute({ projectId: activeProjectId }, session).then(
      (resultado) => {
        if (!vivo) return;
        if (resultado.ok) {
          setLidos({
            de: activeProjectId,
            precos: mapaDePrecos(resultado.value.prices),
            ...(resultado.value.period === undefined ? {} : { periodo: resultado.value.period }),
          });
          void lerFatores(activeProjectId, codigosDosPrecos(resultado.value.prices));
        } else {
          setErro(messageForCode(resultado.error.code, resultado.error.message));
        }
      },
      () => {
        if (vivo) setErro('Os preços não foram lidos — verifique a conexão.');
      },
    );
    return () => {
      vivo = false;
    };
    // A sessão muda de objeto sem mudar de pessoa; quem decide reler é a pessoa e a obra —
    // e o aviso de quem gravou por fora.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container, session?.userId, activeProjectId, releitura]);

  const daObra = lidos !== undefined && lidos.de === activeProjectId ? lidos : undefined;

  const definir = useCallback(
    async (key: string, decisao: DecisaoDePreco, code?: string): Promise<boolean> => {
      if (session === undefined || activeProjectId === undefined) return false;
      const projectId = activeProjectId;
      try {
        const resultado = await container.setMaterialPrice.execute(
          { projectId, key, ...(code === undefined ? {} : { code }), ...decisao },
          session,
        );
        if (!resultado.ok) {
          toast.error(messageForCode(resultado.error.code, resultado.error.message));
          return false;
        }
        if (obraAtual.current !== projectId) return false;
        setLidos((atual) =>
          atual?.de === projectId
            ? { ...atual, precos: comPreco(atual.precos, key, resultado.value.price) }
            : atual,
        );
        return true;
      } catch {
        toast.error('O preço não foi gravado — verifique a conexão.');
        return false;
      }
    },
    [container, session, activeProjectId],
  );

  const atualizar = useCallback(
    async (
      codes: readonly string[],
      periodo: SupplyPricePeriod,
    ): Promise<AtualizacaoFeita | undefined> => {
      if (session === undefined || activeProjectId === undefined || codes.length === 0) {
        return undefined;
      }
      const projectId = activeProjectId;
      setAtualizando(true);
      const feito = { comMedia: 0, semMedia: 0, naoEncontrados: 0 };
      const somar = (contagem: Omit<ResultadoDaAtualizacao, 'integrado'>): void => {
        feito.comMedia += contagem.comMedia;
        feito.semMedia += contagem.semMedia;
        feito.naoEncontrados += contagem.naoEncontrados;
      };
      try {
        // Um lote de cada vez: o compras responde em até 30 s por pedido, e dez pedidos ao
        // mesmo tempo seriam dez transações esperando a mesma tabela.
        for (const lote of emLotes(codes)) {
          const resultado = await container.refreshMaterialPrices
            .execute({ projectId, codes: lote, from: periodo.from, to: periodo.to }, session)
            .catch(() => undefined);
          if (resultado === undefined) {
            return {
              integrado: true,
              ...feito,
              erro: 'O compras não foi consultado — verifique a conexão.',
            };
          }
          if (!resultado.ok) {
            return {
              integrado: true,
              ...feito,
              erro: messageForCode(resultado.error.code, resultado.error.message),
            };
          }
          // Sem integração não há o que tentar no próximo lote: é a mesma resposta.
          if (!resultado.value.integrated) return { integrado: false, ...feito };
          somar(contarFotos(resultado.value.prices));
          if (obraAtual.current !== projectId) continue;
          const novos = resultado.value.prices;
          setLidos((atual) =>
            atual?.de === projectId
              ? {
                  ...atual,
                  precos: comPrecos(atual.precos, novos),
                  // A foto nova é a mais recente: o período dela passa a ser o da obra (D4).
                  ...(novos.length === 0 ? {} : { periodo }),
                }
              : atual,
          );
          // A foto nova pode ter trazido a unidade do compras de um código que a tela não
          // conhecia: o fator do produto dele vem junto, ou o preço ficaria "falta o fator".
          await lerFatores(projectId, codigosDosPrecos(novos));
        }
        return { integrado: true, ...feito };
      } finally {
        setAtualizando(false);
      }
    },
    [container, session, activeProjectId, lerFatores],
  );

  const definirFatorDoProduto = useCallback(
    async (fator: DecisaoDeFator): Promise<boolean> => {
      if (session === undefined || activeProjectId === undefined) return false;
      const projectId = activeProjectId;
      try {
        const resultado = await container.setProductUnitFactors.execute(
          { projectId, factors: [fator] },
          session,
        );
        if (!resultado.ok) {
          toast.error(messageForCode(resultado.error.code, resultado.error.message));
          return false;
        }
        if (obraAtual.current !== projectId) return false;
        const chave = productFactorKey(fator.code, fator.itemUnit);
        // O servidor não devolve o fator que não mudou: a tela monta a linha do pedido.
        const gravado: ProductUnitFactor | undefined =
          fator.factor === null
            ? undefined
            : (resultado.value.saved.find(
                (salvo) => productFactorKey(salvo.code, salvo.itemUnit) === chave,
              ) ?? {
                codeKey: materialCodeKey(fator.code),
                code: fator.code,
                itemUnit: canonicalUnit(fator.itemUnit),
                ...(fator.supplyUnit === undefined ? {} : { supplyUnit: fator.supplyUnit }),
                factor: fator.factor,
                basis: fator.basis,
              });
        setFatores((atual) => {
          const proximo = new Map(atual?.de === projectId ? atual.fatores : SEM_FATORES);
          if (gravado === undefined) proximo.delete(chave);
          else proximo.set(chave, gravado);
          return { de: projectId, fatores: proximo };
        });
        return true;
      } catch {
        toast.error('O fator não foi gravado — verifique a conexão.');
        return false;
      }
    },
    [container, session, activeProjectId],
  );

  return {
    ...(daObra === undefined ? {} : { obra: daObra.de }),
    precos: daObra?.precos,
    periodo: daObra?.periodo ?? dozeMeses,
    podeEditar,
    atualizando,
    ...(erro === undefined ? {} : { erro }),
    definir,
    atualizar,
    fatoresDoProduto:
      fatores !== undefined && fatores.de === activeProjectId ? fatores.fatores : SEM_FATORES,
    definirFatorDoProduto,
  };
}

/** O tempo que o rascunho tem para assentar antes de a tela perguntar ao compras. */
const ESPERA_DOS_CODIGOS_NOVOS = 1200;

/**
 * Busca sozinha a foto dos códigos que chegaram sem ela (D5).
 *
 * A planilha que traz o código de compras — na importação, ou o item colado de outra obra —
 * informa o código tanto quanto quem o escolhe na busca, e o preço tem de vir junto. Sem isto, o
 * orçamento mostrava "preço ainda não buscado" em todas as linhas até alguém achar o botão.
 *
 * Só quem pode editar dispara (a foto é gravação); sem integração com o compras, para de tentar;
 * cada código é tentado uma vez por obra nesta sessão (`codigosSemFoto`). Espera o rascunho
 * assentar: a importação despacha várias ações seguidas, e cada uma refaria a pergunta.
 */
export function useFotoDosCodigosNovos(
  precos: PrecosDaObraNaTela | undefined,
  codigosDaObra: () => readonly string[],
  versaoDoRascunho: unknown,
): void {
  const activeProjectId = precos?.obra;
  const tentados = useRef(new Map<string, Set<string>>());
  const semIntegracao = useRef(false);
  const lidos = precos?.precos;
  const podeEditar = precos?.podeEditar ?? false;
  const atualizar = precos?.atualizar;
  const periodo = precos?.periodo;
  const codigos = useRef(codigosDaObra);
  codigos.current = codigosDaObra;

  useEffect(() => {
    if (
      lidos === undefined ||
      !podeEditar ||
      atualizar === undefined ||
      periodo === undefined ||
      activeProjectId === undefined ||
      semIntegracao.current
    ) {
      return undefined;
    }
    const espera = setTimeout(() => {
      let daObra = tentados.current.get(activeProjectId);
      if (daObra === undefined) {
        daObra = new Set();
        tentados.current.set(activeProjectId, daObra);
      }
      const faltam = codigosSemFoto(codigos.current(), lidos, daObra);
      if (faltam.length === 0) return;
      for (const codigo of faltam) daObra.add(materialCodeKey(codigo));
      void atualizar(faltam, periodo).then((feito) => {
        if (feito === undefined) return;
        if (!feito.integrado) {
          semIntegracao.current = true;
          return;
        }
        if (feito.erro !== undefined) {
          toast.warning(`O preço dos códigos novos não foi buscado: ${feito.erro}`);
        }
      });
    }, ESPERA_DOS_CODIGOS_NOVOS);
    return () => clearTimeout(espera);
  }, [lidos, podeEditar, atualizar, periodo, activeProjectId, versaoDoRascunho]);
}
